import React, { useMemo } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import EmptyStateAction from '@/common/components/EmptyStateAction';
import LightDataGrid from '@/common/components/LightDataGrid';
import styles from '@/modules/financials/financials.styles';
import { Stack } from '@mui/material';

const SECTION_TYPES = {
  institutions: 'institutions',
  liabilities: 'liabilities',
  creditCards: 'creditCards',
};

const mapRows = (items = []) =>
  items.map(({ id, value = {} }, index) => ({
    id: id || index,
    ...value,
  }));

const FinancialSection = ({
  type,
  title,
  data,
  columns,
  emptyState,
  onEdit,
  addEmptyStateSlot,
  headerActionSlot,
}) => {
  const { t } = useTranslation();

  const isEmpty = useMemo(() => {
    if (type === SECTION_TYPES.institutions) {
      return !data?.length;
    }

    return !(data?.list || []).length;
  }, [data, type]);

  if (isEmpty) {
    return (
      <Box sx={styles.section}>
        <Box sx={styles.sectionBody}>
          <EmptyStateAction
            description={emptyState.description}
            actionText={emptyState.actionText}
          >
            {addEmptyStateSlot}
          </EmptyStateAction>
        </Box>
      </Box>
    );
  }

  if (type === SECTION_TYPES.institutions) {
    return (
      <>
        {data.map((item) => (
          <Box sx={styles.section} key={item.id}>
            <Box sx={styles.sectionBody}>
              <Box sx={styles.sectionHeader}>
                <Typography component="h5" sx={styles.sectionTitle}>
                  {item.name}
                </Typography>
                <Button
                  color="success"
                  size="medium"
                  onClick={() => onEdit?.(item)}
                  startIcon={<EditIcon />}
                >
                  {t('Edit')}
                </Button>
              </Box>
              <Stack gap={6} mt={4}>
                <LightDataGrid
                  rows={mapRows(item.assets?.list)}
                  columns={columns.assets}
                  emptyText={t('No assets yet.')}
                />
                <LightDataGrid
                  rows={mapRows(item.liabilities?.list)}
                  columns={columns.liabilities}
                  emptyText={t('No liabilities yet.')}
                />
                <LightDataGrid
                  rows={mapRows(item.creditCards?.list)}
                  columns={columns.creditCards}
                  emptyText={t('No credit cards yet.')}
                />
              </Stack>
            </Box>
          </Box>
        ))}
      </>
    );
  }

  return (
    <Box sx={styles.section}>
      <Box sx={styles.sectionBody}>
        <Box sx={styles.sectionHeader}>
          <Typography component="h5" sx={styles.sectionTitle}>
            {title}
          </Typography>
          {headerActionSlot || (
            <Button color="success" size="medium" onClick={onEdit}>
              <EditIcon fontSize="small" />
              {t('Edit')}
            </Button>
          )}
        </Box>
        <Box sx={styles.dataGridWrap}>
          <LightDataGrid rows={mapRows(data?.list)} columns={columns} />
        </Box>
      </Box>
    </Box>
  );
};

export default FinancialSection;
